/**
 * THE EDITOR - Tiptap over exactly the formatting the platform stores, with our toolbar and our
 * keyboard contract. The cabinet mounts one per text and quote block (ADR-0008; register R-002).
 *
 * THE KEYBOARD CONTRACT, which is what this file was first written to hold:
 *   - Enter it:  one Tab stop from the preceding control.
 *   - Inside it: Tab and Shift-Tab indent and outdent. They do NOT leave.
 *   - Leave it:  ESCAPE moves focus to the toolbar, from where Tab continues normally. With a
 *                menu open inside the editor the first Escape closes the menu and the second
 *                leaves - there is no such menu yet, and the slash menu that will must close on
 *                its own first Escape rather than swallowing the exit.
 *   - The exit must be DISCOVERABLE: a visually hidden instruction associated with the text area
 *     and read on focus. WCAG does not accept an undocumented exit.
 *   - Escape is NEVER bound to "discard" or "close". It is the exit key and nothing else.
 * (docs/ui/03-components.md; web/ui/test/editor/keyboard-escape.spec.tsx holds it.)
 *
 * ★★ JSON IN, JSON OUT - NEVER HTML. It used to answer `onUpdate(html)`, which is exactly what the
 * contract does not take: "There is no `rawHTML` anywhere in this contract: the editor's HTML never
 * reaches the API" (authors-api.yaml's `TextBlock`). So the document goes in and comes out as
 * ProseMirror JSON, and `shared/content-model/editor-json.ts` turns that into a block's `body`, its
 * paragraph rows and its mark rows - code-point offsets computed by the same functions the server
 * checks them with. This file knows nothing about blocks; the screen does the converting.
 *
 * ★ `initialContent` IS READ ONCE, WHEN THE EDITOR MOUNTS. Feeding each change back in would reset
 * the cursor on every keystroke; a caller that must REPLACE the document (the author took the
 * server's copy in a conflict) mounts a new editor with a new `key`.
 *
 * ★ A DOCUMENT THE SCHEMA CANNOT HOLD IS SHOWN READ-ONLY AND NEVER SAVED. Tiptap's default for
 * invalid content is to log a warning and open an EMPTY document - and the next keystroke would
 * then report that empty document as the author's text. `enableContentCheck` turns it into an
 * event; this component then refuses edits and says so, and `onChange` is never called.
 *
 * WHY NOT @mantine/tiptap's RichTextEditor. Read from that package's source, its toolbar is a bare
 * box - no `role="toolbar"`, no label, no roving focus, no arrow keys - and the accessibility
 * above is ours either way (ADR-0008: "we own the chrome and its accessibility").
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Button, Group, Input, TextInput } from '@mantine/core';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import type { Editor, JSONContent } from '@tiptap/react';
import { EditorToolbar } from './EditorToolbar';
import type { EditorToolbarHandle } from './EditorToolbar';
import { HEADING_LEVELS, editorExtensions } from './extensions';
import type { HeadingLevel } from './extensions';
import { useUiText } from '../../texts/ui-texts';
import type { UiTranslate } from '../../texts/ui-texts';
import classes from './Editor.module.css';

/** One ProseMirror mark as JSON. Structurally the content model's `EditorMark`. */
export interface RichTextMark {
  readonly type: string;
  readonly attrs?: Readonly<Record<string, unknown>> | undefined;
}

/** One ProseMirror node as JSON - what `editor.getJSON()` answers. Structurally `EditorNode`. */
export interface RichTextNode {
  readonly type?: string | undefined;
  readonly attrs?: Readonly<Record<string, unknown>> | undefined;
  readonly content?: readonly RichTextNode[] | undefined;
  readonly text?: string | undefined;
  readonly marks?: readonly RichTextMark[] | undefined;
}

export interface EditorShellProps {
  /** The accessible name of the text area itself. */
  label: string;
  /** The accessible name of the toolbar. Distinct from the text area's, or both read the same. */
  toolbarLabel?: string;
  /** The document it opens with. Read once, at mount - see the header. */
  initialContent?: RichTextNode;
  /** Every change the author makes, as ProseMirror JSON. */
  onChange?: (doc: RichTextNode) => void;
  /** Headings and lists as well as the inline marks. Off for a quotation. Read once, at mount. */
  paragraphStyles?: boolean;
  /** Which link addresses may be stored. Without it there is no Link control and no link. */
  isAllowedLink?: ((href: string) => boolean) | undefined;
  /** The deepest list nesting Tab may make, 0-based. Without it Tab nests without limit. */
  maxListLevel?: number | undefined;
  /** Nothing can be typed and no control acts. */
  disabled?: boolean;
  /** A sentence under the editor, read with it. */
  error?: ReactNode | undefined;
}

/** Everything the toolbar draws as pressed, read from the editor's state on every transaction. */
interface ActiveState {
  readonly [control: string]: boolean;
}

interface StyleControl {
  /** Which control it is: its key in the pressed state. Never shown. */
  readonly id: string;
  /** What its button says. */
  readonly words: (t: UiTranslate) => string;
  readonly isActive: (editor: Editor) => boolean;
  readonly apply: (editor: Editor) => void;
}

const LIST_NODES = new Set(['bulletList', 'orderedList']);

/** 0 in a top-level list, 1 in a list inside it, and so on; -1 outside every list. */
function listLevelAt(editor: Editor): number {
  const { $from } = editor.state.selection;
  let lists = 0;
  for (let depth = $from.depth; depth > 0; depth -= 1) {
    if (LIST_NODES.has($from.node(depth).type.name)) {
      lists += 1;
    }
  }
  return lists - 1;
}

/**
 * Out of every list the selection is in. A heading cannot be the first child of a list item in the
 * kit's schema, so "make this a heading" inside a list lifts it out first. Bounded: a list nests at
 * most as deep as the schema lets Tab make it, and a lift that fails stops the loop.
 */
function leaveLists(editor: Editor): void {
  for (let guard = 0; guard < 16 && listLevelAt(editor) >= 0; guard += 1) {
    if (!editor.chain().focus().liftListItem('listItem').run()) {
      return;
    }
  }
}

function styleControls(): readonly StyleControl[] {
  return [
    {
      id: 'paragraph',
      words: (t) => t('editor.paragraph'),
      isActive: (editor) => editor.isActive('paragraph') && listLevelAt(editor) < 0,
      apply: (editor) => {
        leaveLists(editor);
        editor.chain().focus().setParagraph().run();
      },
    },
    ...HEADING_LEVELS.map((level: HeadingLevel): StyleControl => ({
      id: `heading${String(level)}`,
      words: (t) => t('editor.heading', { level }),
      isActive: (editor) => editor.isActive('heading', { level }),
      apply: (editor) => {
        leaveLists(editor);
        editor.chain().focus().setHeading({ level }).run();
      },
    })),
    {
      id: 'bulletList',
      words: (t) => t('editor.bulletedList'),
      isActive: (editor) => editor.isActive('bulletList'),
      apply: (editor) => {
        editor.chain().focus().toggleBulletList().run();
      },
    },
    {
      id: 'orderedList',
      words: (t) => t('editor.numberedList'),
      isActive: (editor) => editor.isActive('orderedList'),
      apply: (editor) => {
        editor.chain().focus().toggleOrderedList().run();
      },
    },
  ];
}

const MARK_CONTROLS: readonly StyleControl[] = [
  {
    id: 'bold',
    words: (t) => t('editor.bold'),
    isActive: (editor) => editor.isActive('bold'),
    apply: (editor) => {
      editor.chain().focus().toggleBold().run();
    },
  },
  {
    id: 'italic',
    words: (t) => t('editor.italic'),
    isActive: (editor) => editor.isActive('italic'),
    apply: (editor) => {
      editor.chain().focus().toggleItalic().run();
    },
  },
  {
    id: 'underline',
    words: (t) => t('editor.underline'),
    isActive: (editor) => editor.isActive('underline'),
    apply: (editor) => {
      editor.chain().focus().toggleUnderline().run();
    },
  },
  {
    id: 'strike',
    words: (t) => t('editor.strikethrough'),
    isActive: (editor) => editor.isActive('strike'),
    apply: (editor) => {
      editor.chain().focus().toggleStrike().run();
    },
  },
  {
    id: 'code',
    words: (t) => t('editor.code'),
    isActive: (editor) => editor.isActive('code'),
    apply: (editor) => {
      editor.chain().focus().toggleCode().run();
    },
  },
];

const LINK_CONTROL = 'link';

/**
 * What an author typed into the link box, as an address. A bare `moonx.art` is an `https://`
 * address and a bare `name@moonx.art` a `mailto:` one; anything with a scheme of its own is kept as
 * typed, so the caller's rule - not a guess here - decides whether it is stored. `host:8080` is not
 * a scheme, which is what the `(?!\d)` is for.
 */
export function linkHrefFrom(typed: string): string {
  const value = typed.trim();
  if (/^[a-z][a-z0-9+.-]*:(?!\d)/iu.test(value)) {
    return value;
  }
  if (/^[^\s@/]+@[^\s@/]+\.[^\s@/]+$/u.test(value)) {
    return `mailto:${value}`;
  }
  return `https://${value}`;
}

export function EditorShell({
  label,
  toolbarLabel,
  initialContent,
  onChange,
  paragraphStyles = true,
  isAllowedLink,
  maxListLevel,
  disabled = false,
  error,
}: EditorShellProps) {
  const t = useUiText();
  const toolbar = useRef<EditorToolbarHandle>(null);
  const hintId = useId();
  const errorId = useId();
  const linkInputId = useId();

  /* The newest callbacks, for handlers the editor was created with. */
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const isAllowedLinkRef = useRef(isAllowedLink);
  isAllowedLinkRef.current = isAllowedLink;
  const maxListLevelRef = useRef(maxListLevel);
  maxListLevelRef.current = maxListLevel;

  /* ★ SET BY TIPTAP WHILE THE EDITOR IS BEING BUILT, which is inside a render - so a ref, read by
     the effect below, and never a state update from inside Tiptap's constructor. */
  const contentFailed = useRef(false);
  /* The document as the author last left it - see the effect that reads it. */
  const latestDoc = useRef<JSONContent | null>(null);
  const [unreadable, setUnreadable] = useState(false);
  const [linkDraft, setLinkDraft] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  const linksOn = isAllowedLink !== undefined;
  // Read once: a mounted editor's schema cannot change (see `paragraphStyles` above).
  const [schemaOptions] = useState(() => ({ paragraphStyles, linksOn }));
  const extensions = useMemo(
    () =>
      editorExtensions({
        paragraphStyles: schemaOptions.paragraphStyles,
        isAllowedLink: schemaOptions.linksOn
          ? (href: string) => isAllowedLinkRef.current?.(href) ?? false
          : undefined,
      }),
    [schemaOptions],
  );

  const hasError = unreadable || (error !== undefined && error !== null && error !== false);
  const editorProps = useMemo(
    () => ({
      attributes: {
        /* Tiptap supplies exactly one of the roles this contract needs - `role="textbox"` - and
           zero `aria-` attributes in 285 KB of distributed source. The rest are these. */
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': label,
        // The sentence under the editor is read with it only while there is one.
        'aria-describedby': hasError ? `${hintId} ${errorId}` : hintId,
        class: [classes.content, paragraphStyles ? '' : classes.contentShort]
          .filter((name) => name !== undefined && name !== '')
          .join(' '),
      },
      handleKeyDown: (_view: unknown, event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          /* THE EXIT. Focus moves to the toolbar and the event stops here, so nothing else can
             read Escape as "discard". */
          event.preventDefault();
          toolbar.current?.focusFirst();
          return true;
        }
        if (event.key === 'Tab') {
          /* Writers expect Tab to indent a list item, so Tab cannot be the way out - and an
             editor where Tab neither indents nor escapes is the trap itself. Returning true
             claims the key in every case, which is what stops focus leaving. */
          event.preventDefault();
          return true;
        }
        return false;
      },
    }),
    [label, hintId, errorId, hasError, paragraphStyles],
  );

  /* ★ `Editor | null`, WHATEVER THE OVERLOAD SAYS. Tiptap destroys an editor whose component has
     stayed unmounted past a tick and builds a new one when it mounts again - an `<Activity>` hide
     does exactly that - and in between the hook answers null. Every use below is guarded. */
  const editor: Editor | null = useEditor({
    extensions,
    content: (initialContent ?? { type: 'doc', content: [{ type: 'paragraph' }] }) as JSONContent,
    editable: !disabled,
    enableContentCheck: true,
    onContentError: () => {
      contentFailed.current = true;
    },
    editorProps,
    onUpdate: ({ editor: instance }) => {
      if (contentFailed.current || !instance.isEditable) {
        return;
      }
      const doc = instance.getJSON();
      latestDoc.current = doc;
      onChangeRef.current?.(doc);
    },
  });

  /* ★ A REBUILT EDITOR STARTS FROM THE AUTHOR'S TEXT, NOT FROM THE MOUNT'S. Tiptap builds the new
     instance from the options it was last given, and `content` there is `initialContent` - the
     document as it was when this component mounted. Left alone, the author would see their older
     text, and the next keystroke would report it as the newest: the edits made since mounting,
     gone. So the last document this editor reported is put back, without reporting it again. */
  useEffect(() => {
    if (editor === null) {
      return;
    }
    if (contentFailed.current) {
      setUnreadable(true);
      return;
    }
    if (latestDoc.current !== null) {
      editor.commands.setContent(latestDoc.current, { emitUpdate: false });
    }
  }, [editor]);

  const editable = !disabled && !unreadable;
  useEffect(() => {
    // `false`: the second argument is Tiptap's "emit an update", which would report an unchanged
    // document to `onChange` as though the author had typed it - and schedule a save of nothing.
    editor?.setEditable(editable, false);
    if (!editable) {
      setLinkDraft(null);
    }
  }, [editor, editable]);

  /* Tab and Shift-Tab indent and outdent inside a list. These commands only apply in one, and
     `handleKeyDown` above has already stopped Tab from moving focus, so outside a list Tab is
     simply inert rather than an escape hatch. ★ No deeper than `maxListLevel`: the platform keeps
     at most three levels below the first (`ck_awpara_level`), so a fourth Tab does nothing rather
     than draw a nesting the save would flatten. */
  useEffect(() => {
    if (editor === null || !schemaOptions.paragraphStyles) {
      return undefined;
    }
    const element = editor.view.dom;
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Tab' || !editor.isEditable) {
        return;
      }
      if (event.shiftKey) {
        editor.chain().focus().liftListItem('listItem').run();
        return;
      }
      const level = listLevelAt(editor);
      const limit = maxListLevelRef.current;
      if (level >= 0 && (limit === undefined || level < limit)) {
        editor.chain().focus().sinkListItem('listItem').run();
      }
    };
    element.addEventListener('keydown', onKeyDown);
    return () => {
      element.removeEventListener('keydown', onKeyDown);
    };
  }, [editor, schemaOptions]);

  const controls = useMemo(
    () => [...(schemaOptions.paragraphStyles ? styleControls() : []), ...MARK_CONTROLS],
    [schemaOptions],
  );

  const active = useEditorState({
    editor,
    selector: ({ editor: instance }): ActiveState => {
      const state: Record<string, boolean> = {};
      for (const control of controls) {
        state[control.id] = control.isActive(instance);
      }
      state[LINK_CONTROL] = schemaOptions.linksOn && instance.isActive('link');
      return state;
    },
  });

  const openLink = () => {
    if (editor === null) {
      return;
    }
    const current: unknown = editor.getAttributes('link')['href'];
    setLinkError(null);
    setLinkDraft(typeof current === 'string' ? current : '');
  };

  const closeLink = () => {
    setLinkDraft(null);
    setLinkError(null);
    editor?.chain().focus().run();
  };

  const applyLink = () => {
    if (editor === null) {
      return;
    }
    const typed = (linkDraft ?? '').trim();
    if (typed.length === 0) {
      setLinkError(t('editor.linkEmpty'));
      return;
    }
    const href = linkHrefFrom(typed);
    if (!(isAllowedLinkRef.current?.(href) ?? false)) {
      setLinkError(t('editor.linkRefused'));
      return;
    }
    if (editor.state.selection.empty && !editor.isActive('link')) {
      // Nothing selected: the address itself goes in, as the link's words.
      editor
        .chain()
        .focus()
        .insertContent({ type: 'text', text: typed, marks: [{ type: 'link', attrs: { href } }] })
        .run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    }
    setLinkDraft(null);
    setLinkError(null);
  };

  const removeLink = () => {
    editor?.chain().focus().extendMarkRange('link').unsetLink().run();
    setLinkDraft(null);
    setLinkError(null);
  };

  const button = (id: string, words: string, onPress: () => void, pressed: boolean) => (
    <button
      key={id}
      type="button"
      className={classes.control}
      aria-pressed={pressed}
      disabled={!editable || editor === null}
      // Keeps the text selection and focus where the author left them when a pointer presses.
      onMouseDown={(event) => {
        event.preventDefault();
      }}
      onClick={onPress}
    >
      {words}
    </button>
  );

  return (
    <div className={classes.root}>
      <EditorToolbar ref={toolbar} label={toolbarLabel ?? t('editor.toolbar')}>
        {controls.map((control) =>
          button(
            control.id,
            control.words(t),
            () => {
              if (editor !== null) {
                control.apply(editor);
              }
            },
            active?.[control.id] ?? false,
          ),
        )}
        {schemaOptions.linksOn
          ? button(LINK_CONTROL, t('editor.link'), openLink, active?.[LINK_CONTROL] ?? false)
          : null}
      </EditorToolbar>

      {linkDraft === null ? null : (
        <div className={classes.linkForm} role="group" aria-label={t('editor.linkGroup')}>
          <TextInput
            id={linkInputId}
            label={t('editor.linkAddress')}
            description={t('editor.linkAddressHint')}
            value={linkDraft}
            error={linkError}
            autoFocus
            onChange={(event) => {
              setLinkDraft(event.currentTarget.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                applyLink();
              }
            }}
          />
          <Group gap="xs">
            <Button size="compact-sm" onClick={applyLink}>
              {t('editor.applyLink')}
            </Button>
            {active?.[LINK_CONTROL] === true ? (
              <Button size="compact-sm" variant="default" onClick={removeLink}>
                {t('editor.removeLink')}
              </Button>
            ) : null}
            <Button size="compact-sm" variant="subtle" onClick={closeLink}>
              {t('editor.cancel')}
            </Button>
          </Group>
        </div>
      )}

      {/* Read on focus, because an exit nobody is told about is not an exit. */}
      <span id={hintId} className={classes.hint}>
        {t('editor.exitHint')}
      </span>

      <EditorContent editor={editor} />

      <div id={errorId}>
        {unreadable ? (
          <Input.Error>{t('editor.unreadable')}</Input.Error>
        ) : error === undefined || error === null || error === false ? null : (
          <Input.Error>{error}</Input.Error>
        )}
      </div>
    </div>
  );
}
